import {
  Box,
  TableCell,
  TableHead,
  TableRow,
  TableSortLabel,
} from '@mui/material';
import { visuallyHidden } from '@mui/utils';
import { Order } from './sortingFunctions';
import { ColumnConfiguration } from '../types';

export interface SortableTableHeadersProps<T> {
  columnConfig: readonly ColumnConfiguration<T>[];
  hasActionCell?: boolean;
  order: Order;
  orderBy: string;
  onRequestSort: (event: React.MouseEvent<unknown>, property: string) => void;
}

export const SortableTableHeaders = <T,>({
  columnConfig,
  hasActionCell = false,
  order,
  orderBy,
  onRequestSort,
}: SortableTableHeadersProps<T>) => {
  const createSortHandler =
    (property: keyof T) => (event: React.MouseEvent<unknown>) => {
      onRequestSort(event, String(property));
    };

  return (
    <TableHead
      sx={{
        '& .MuiTableCell-root': {
          minWidth: 'fit-content',
          wordBreak: 'normal',
        },
      }}
    >
      <TableRow>
        {columnConfig.map(column => (
          <TableCell
            key={column.id as string}
            align={column.align || (column.isNumeric ? 'right' : 'left')}
            padding={column.shouldDisablePadding ? 'none' : 'normal'}
            sortDirection={orderBy === column.id ? order : false}
            size="small"
            sx={{
              width: column.width || 'auto',
              minWidth: column.width || 'fit-content',
              whiteSpace: column.noWrap ? 'nowrap' : 'normal',
            }}
          >
            <TableSortLabel
              active={orderBy === column.id}
              disabled={!column.sortable}
              direction={orderBy === column.id ? order : 'asc'}
              onClick={
                column.sortable ? createSortHandler(column.id) : undefined
              }
            >
              {column.title}
              {orderBy === column.id ? (
                <Box component="span" sx={visuallyHidden}>
                  {order === 'desc' ? 'sorted descending' : 'sorted ascending'}
                </Box>
              ) : null}
            </TableSortLabel>
          </TableCell>
        ))}
        {hasActionCell && (
          <TableCell
            key="actions-cell"
            align="left"
            padding="none"
            sortDirection={false}
          >
            <TableSortLabel disabled></TableSortLabel>
          </TableCell>
        )}
      </TableRow>
    </TableHead>
  );
};
